const DEFAULT_TIMEOUT_MS = 30000;
const MAX_RETRIES = 2;
const RETRY_BACKOFF = [1000, 3000];

function isRetryableStatus(status: number): boolean {
  return status >= 500 && status < 600;
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  options?: { timeout?: number; retries?: number },
): Promise<Response> {
  const token = localStorage.getItem('token');
  const headers = new Headers(init.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('X-Client-UI', 'shop');

  const timeoutMs = options?.timeout ?? DEFAULT_TIMEOUT_MS;
  const maxRetries = options?.retries ?? MAX_RETRIES;

  let lastResponse: Response | undefined;
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(input, { ...init, headers }, timeoutMs);

      // Handle 401 — session expired
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userRoleLevel');
        window.location.reload();
        return response;
      }

      // Don't retry client errors (4xx)
      if (response.status >= 400 && response.status < 500) {
        return response;
      }

      // Retry on 5xx
      if (isRetryableStatus(response.status) && attempt < maxRetries) {
        lastResponse = response;
        await sleep(RETRY_BACKOFF[attempt] ?? 3000);
        continue;
      }

      return response;
    } catch (err) {
      lastError = err;
      // Retry on network errors
      if (attempt < maxRetries) {
        await sleep(RETRY_BACKOFF[attempt] ?? 3000);
        continue;
      }
    }
  }

  // If we exhausted retries and have a response, return it
  if (lastResponse) return lastResponse;

  // Otherwise throw the last network error
  throw lastError;
}
